import React, { useState } from "react";
import { PodcastEpisode, CATEGORIES } from "@/data/podcasts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface FeaturedEpisodesProps {
  episodes: PodcastEpisode[];
  onSelectEpisode?: (episode: PodcastEpisode) => void;
}

export const FeaturedEpisodes: React.FC<FeaturedEpisodesProps> = ({
  episodes,
  onSelectEpisode,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(
    "Identity & Becoming",
  );
  const [activeModalEpisode, setActiveModalEpisode] =
    useState<PodcastEpisode | null>(null);

  const filteredEpisodes =
    selectedCategory === "All"
      ? episodes.filter((e) => !e.isRecent)
      : episodes.filter((e) => !e.isRecent && e.category === selectedCategory);

  const handleWatch = (ep: PodcastEpisode) => {
    setActiveModalEpisode(ep);
    if (onSelectEpisode) {
      onSelectEpisode(ep);
    }
  };

  return (
    <section className="bg-white py-16 md:py-24 px-4 border-y border-[#E8DEC8]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic mb-2">
            Featured Guests
          </p>
          <h2 className="text-3xl md:text-5xl font-normal text-[#380C1E] mb-4 font-sans">
            EVERY FACE HERE TRUSTED THIS SHOW WITH THEIR STORY.
          </h2>
          <p className="text-sm md:text-base text-[#380C1E]/80 max-w-2xl mx-auto font-light leading-relaxed">
            Many individuals have sat across from Ieshia since 2023. Here are
            featured conversations to start with.
          </p>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
                selectedCategory === cat
                  ? "bg-[#9E1B50] text-white shadow-sm"
                  : "bg-[#FBF1F4] text-[#380C1E] hover:bg-[#F5E9EC]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid of Guest Circular Thumbnails */}
        {filteredEpisodes.length === 0 ? (
          <div className="text-center py-12 text-sm text-[#380C1E]/60 italic">
            No episodes found in this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8 mb-12">
            {filteredEpisodes.map((ep) => (
              <div
                key={ep.id}
                className="flex flex-col items-center text-center group"
              >
                <button
                  onClick={() => handleWatch(ep)}
                  className="w-full aspect-[4/2.5] mb-4 border-2 border-[#C9A227] p-1 bg-white shadow-sm group-hover:scale-105 transition-transform overflow-hidden relative cursor-pointer block text-left"
                  aria-label={`Play ${ep.episodeTitle}`}
                >
                  <div
                    className="w-full h-full bg-cover bg-center overflow-hidden relative"
                    style={{ backgroundImage: `url(${ep.thumbnailUrl})` }}
                  >
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-[#9E1B50]/90 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  {ep.views && (
                    <span className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                      {ep.views}
                    </span>
                  )}
                </button>
                <h4 className="font-bold text-[#380C1E] mb-1 text-sm sm:text-base">
                  {ep.guestName}
                </h4>
                <h5 className="font-serif font-bold text-xs uppercase tracking-wider text-[#9E1B50] mb-2 leading-snug line-clamp-2">
                  {ep.episodeTitle}
                </h5>
                <p className="text-[#380C1E]/80 italic text-xs mb-3 line-clamp-2">
                  "{ep.quote}"
                </p>
                <button
                  onClick={() => handleWatch(ep)}
                  className="text-[#9E1B50] text-xs font-semibold hover:underline flex items-center gap-1"
                >
                  Watch her story →
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="text-center pt-4">
          <p className="text-xs text-[#380C1E]/70 mb-4 font-light">
            These are a beginning, not a hall of fame. Every individual who ever
            sat in that chair mattered.
          </p>
          <a
            href="https://www.youtube.com/@imieshiadanielle"
            target="_blank"
            rel="noreferrer"
            className="inline-block border border-[#9E1B50] text-[#9E1B50] hover:bg-[#9E1B50] hover:text-white font-bold uppercase text-[11px] tracking-wider px-8 py-3 rounded-full transition-all"
          >
            See Every Conversation on YouTube →
          </a>
        </div>
      </div>

      {/* Embedded Video Dialog Modal */}
      {activeModalEpisode && (
        <Dialog
          open={!!activeModalEpisode}
          onOpenChange={() => setActiveModalEpisode(null)}
        >
          <DialogContent className="max-w-4xl bg-[#4A0E26] text-white border border-white/20 p-6 rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl sm:text-2xl text-white">
                {activeModalEpisode.episodeTitle}
              </DialogTitle>
            </DialogHeader>
            <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black mt-4">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${activeModalEpisode.youtubeVideoId}?autoplay=1`}
                title={activeModalEpisode.episodeTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="mt-4 flex justify-between items-center text-xs text-white/80">
              <p>
                Guest:{" "}
                <span className="font-semibold text-white">
                  {activeModalEpisode.guestName}
                </span>
              </p>
              <a
                href={activeModalEpisode.youtubeUrl}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-white"
              >
                Open in YouTube ↗
              </a>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};
